import { ReactNode } from 'react';
import { Trans } from 'react-i18next';

import {
  APP_BUILDER_BROWSE_TAB_PATH,
  CRM_CLIENTS_PAGE,
  CUSTOMIZATION_PAGE,
  DOMAIN_PAGE,
  PAYMENTS_PAGE,
} from 'src/constants';
import { primaryDomainHostname } from 'src/constants/hostnameConsts';
import { i18n } from 'src/i18n/i18next.config';
import { ensureUnreachable } from 'src/utils/common_utils';

import { TaskCodes } from './constants';

// represents Task detail info which is show when a task is made active on onboarding setup
export interface TaskDetailInfo {
  title: string;
  detailTitle?: string; // use if you want to use a different title in the task details
  description: ReactNode;
  buttonLabel?: string;
  illustrationName: string;
  learnMoreLink: string;
  routePath?: string;
}

interface DescriptionParams {
  currentUserEmail: string;
  portalName: string;
}

// Takes the task code and enrich the details for the given task
// enrichment includes having description text, illustration, learn more link and redirect path for the action button
export function MapTaskCodeToTaskDetails(
  taskCode: TaskCodes,
  isOnboardingTestClient: boolean,
  descriptionParams: DescriptionParams = {
    portalName: '',
    currentUserEmail: '',
  },
): TaskDetailInfo {
  switch (taskCode) {
    case TaskCodes.TaskCodeCustomization:
      return {
        title: i18n.t(($) => $.page_home.onboarding_tasks.customization.title),
        description: i18n.t(
          ($) => $.page_home.onboarding_tasks.customization.description,
        ),
        buttonLabel: i18n.t(
          ($) => $.page_home.onboarding_tasks.customization.button_label,
        ),
        learnMoreLink: `https://www.${primaryDomainHostname.prod}/guide/customization-and-setup#basic-customizations`,
        illustrationName: 'customize_your_portal',
        routePath: CUSTOMIZATION_PAGE.path,
      };
    case TaskCodes.TaskCodeFirstClient:
      if (isOnboardingTestClient) {
        return {
          title: i18n.t(
            ($) => $.page_home.onboarding_tasks.explore_portal.title,
          ),
          detailTitle: i18n.t(
            ($) => $.page_home.onboarding_tasks.explore_portal.title_detail,
          ),
          description: (
            <Trans
              i18nKey={'page_home.onboarding_tasks.explore_portal.description'}
              components={{ Bold: <b /> }}
              values={{
                clientEmail: descriptionParams.currentUserEmail,
                portalName: descriptionParams.portalName,
              }}
            />
          ),
          learnMoreLink: `https://www.${primaryDomainHostname.prod}/guide/test-run`,
          illustrationName: 'explore_your_portal',
        };
      }
      return {
        title: i18n.t(($) => $.page_home.onboarding_tasks.first_client.title),
        description: i18n.t(
          ($) => $.page_home.onboarding_tasks.first_client.description,
        ),
        buttonLabel: i18n.t(
          ($) => $.page_home.onboarding_tasks.first_client.button_label,
        ),
        learnMoreLink: `https://www.${primaryDomainHostname.prod}/guide/test-run`,
        illustrationName: 'create_your_first_client',
        routePath: `${CRM_CLIENTS_PAGE.path}?openCreateModal=true`,
      };
    case TaskCodes.TaskCodeFirstApp:
      return {
        title: i18n.t(($) => $.page_home.onboarding_tasks.first_app.title),
        description: i18n.t(
          ($) => $.page_home.onboarding_tasks.first_app.description,
        ),
        buttonLabel: i18n.t(
          ($) => $.page_home.onboarding_tasks.first_app.button_label,
        ),
        learnMoreLink: `https://www.${primaryDomainHostname.prod}/guide/intro-to-apps`,
        illustrationName: 'add_your_first_app',
        routePath: APP_BUILDER_BROWSE_TAB_PATH,
      };
    case TaskCodes.TaskCodeCustomDomain:
      return {
        title: i18n.t(($) => $.page_home.onboarding_tasks.custom_domain.title),
        description: i18n.t(
          ($) => $.page_home.onboarding_tasks.custom_domain.description,
        ),
        buttonLabel: i18n.t(
          ($) => $.page_home.onboarding_tasks.custom_domain.button_label,
        ),
        learnMoreLink: `https://www.${primaryDomainHostname.prod}/guide/custom-domains`,
        illustrationName: 'connect_your_custom_domain',
        routePath: DOMAIN_PAGE.path,
      };
    case TaskCodes.TaskCodeSetupWithPayments:
      return {
        title: i18n.t(($) => $.page_home.onboarding_tasks.setup_payments.title),
        description: i18n.t(
          ($) => $.page_home.onboarding_tasks.setup_payments.description,
        ),
        buttonLabel: i18n.t(
          ($) => $.page_home.onboarding_tasks.setup_payments.button_label,
        ),
        learnMoreLink: `https://www.${primaryDomainHostname.prod}/guide/copilot-billing-app`,
        illustrationName: 'get_setup_with_payments',
        routePath: PAYMENTS_PAGE.path,
      };
    default:
      ensureUnreachable(taskCode);
  }
}
